import type { SortDirection } from "@trackmark/core";
import { endpoints } from "../api.js";
import { useResource } from "../useResource.js";
import { DEFAULT_SORT, Library, type LibraryTab } from "./Library.js";
import { Failed, Loading } from "./Loading.js";

/**
 * The library, reading the library.
 *
 * The two filtered variants are the same two endpoints with a preset filter and a different sort
 * (§J14) — chosen here rather than on the server's default, so the URL carries the whole state and
 * `NEED TO RATE` is a link somebody can bookmark.
 *
 * Only the tab in view is fetched. Songs and albums are thousands of rows each, and paying for
 * both to render one is the kind of cost that only shows up on the deploy.
 */
export interface LibraryScreenProps {
  tab: LibraryTab;
  onTab: (tab: LibraryTab) => void;
  /** Absent means "the tab's own", which is not the same as any particular column. */
  sort?: string;
  direction?: SortDirection;
  onSort: (sort: string, direction: SortDirection) => void;
}

export function LibraryScreen({ tab, onTab, sort, direction, onSort }: LibraryScreenProps) {
  const songTab = tab === "songs" || tab === "need-to-rate";
  const albumTab = tab === "albums" || tab === "incomplete";

  const songs = useResource(
    () =>
      songTab
        ? endpoints.librarySongs({
            // The server's sort no longer decides the order — the screen does, over the whole
            // list, so a heading can turn it around without a round trip. What is asked for here
            // is only which rows: `unratedOnly` is the tab.
            unratedOnly: tab === "need-to-rate",
          })
        : Promise.resolve({ sort: "title", total: 0, songs: [] }),
    [tab, songTab],
  );

  const albums = useResource(
    () =>
      albumTab
        ? endpoints.libraryAlbums({
            incompleteOnly: tab === "incomplete",
          })
        : Promise.resolve({ sort: "title", total: 0, albums: [] }),
    [tab, albumTab],
  );

  const artists = useResource(
    () => (tab === "artists" ? endpoints.rankingArtists() : Promise.resolve(null)),
    [tab],
  );

  const error = songs.error ?? albums.error ?? artists.error;
  if (error) {
    return (
      <Failed
        error={error}
        retry={() => {
          songs.reload();
          albums.reload();
          artists.reload();
        }}
      />
    );
  }

  if (songs.loading || albums.loading || artists.loading) return <Loading what="the library" />;

  return (
    <Library
      tab={tab}
      onTab={onTab}
      songs={songs.data?.songs ?? []}
      albums={albums.data?.albums ?? []}
      artists={artists.data?.artists ?? []}
      collection={artists.data?.collection ?? { totalRated: 0, mean: null }}
      settings={artists.data?.settings}
      sort={sort ?? DEFAULT_SORT[tab].sort}
      direction={direction ?? DEFAULT_SORT[tab].direction}
      onSort={onSort}
    />
  );
}
